import React, { useState, useEffect, useRef } from 'react';
import './UploadFile.style.css';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useDeleteMenuFile } from 'hooks/MenuFileHooks';
import { useDeleteShopInformationFile } from 'hooks/ShopInformationFileHooks';
import MessageAlert from 'utils';

const PreviewImage = ({ file, style, gap, size, onRemove }) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (file && !file?.created_at) {
      const link = URL.createObjectURL(file);
      setUrl(link);
    }
    return () => URL.revokeObjectURL(file);
  }, [file]);

  return (
    <div
      className="preview_image"
      style={{ width: size, height: size, marginLeft: gap }}
    >
      {file ? (
        <img
          src={
            file?.created_at
              ? `${process.env.REACT_APP_STORE_URL}/${file.file_url}`
              : url
          }
          alt=""
          style={{ ...style }}
        />
      ) : (
        <div></div>
      )}
      {file && (
        <button onClick={() => onRemove(file)}>
          <CloseOutlined />
        </button>
      )}
    </div>
  );
};

const UploadFile = ({
  files = [],
  preview = false,
  max = 1,
  multiple = false,
  size = 63,
  style = {},
  previewStyle = {},
  accept = 'image/*',
  icon = <PlusOutlined />,
  gap = 15,
  onChange,
}) => {
  const fileInputRef = useRef(null);
  const [uploadFiles, setUploadFiles] = useState(files ?? []);
  const { deleteMenuFile } = useDeleteMenuFile();
  const { deleteShopInformationFile } = useDeleteShopInformationFile();

  useEffect(() => {
    setUploadFiles(files); // 외부에서 받은 files prop이 변경되면 상태 업데이트
  }, [files]);

  useEffect(() => {
    if (onChange) {
      onChange(uploadFiles);
    }
  }, [uploadFiles, onChange]);

  const handleFileList = (file, type = 'add') => {
    if (type === 'add') {
      const list = Array.from(file);
      if (list.length + uploadFiles.length > max) {
        MessageAlert.warning(
          `파일은 최대 ${max}개의 파일만 업로드할 수 있습니다.`
        );
        return;
      }
      setUploadFiles((prev) => [...prev, ...list]);
    }
  };

  const handleRemoveFile = (file) => {
    if (file.menu_file_id) {
      // 삭제 확인 후 `true`이면 삭제를 진행
      const confirmDelete = window.confirm('정말 이 파일을 삭제하시겠습니까?');
      if (confirmDelete) {
        deleteMenuFile(file.menu_file_id)
          .then(() => {
            setUploadFiles((prev) => prev.filter((item) => item !== file));
          })
          .catch((error) => {
            console.error('Failed to delete file:', error);
          });
      }
    } else if (file.shop_information_file_id) {
      // 삭제 확인 후 `true`이면 삭제를 진행
      const confirmDelete = window.confirm('정말 이 파일을 삭제하시겠습니까?');
      if (confirmDelete) {
        deleteShopInformationFile(file.shop_information_file_id)
          .then(() => {
            setUploadFiles((prev) => prev.filter((item) => item !== file));
          })
          .catch((error) => {
            console.error('Failed to delete file:', error);
          });
      }
    } else {
      // `menu_file_id`가 없는 경우 상태에서만 파일 삭제
      const confirmDelete = window.confirm('정말 이 파일을 삭제하시겠습니까?');
      if (confirmDelete) {
        setUploadFiles((prev) => prev.filter((item) => item !== file));
      }
    }
  };

  const handleLabelClick = (e) => {
    if (uploadFiles.length >= max) {
      MessageAlert.warning(
        `파일은 최대 ${max}개의 파일만 업로드할 수 있습니다.`
      );
      e.preventDefault();
    } else {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="upload_button_layout">
      <div
        className="upload_button"
        style={{ width: size, height: size, ...style }}
      >
        <label onClick={handleLabelClick}>{icon}</label>
        <input
          ref={fileInputRef}
          id="upload"
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={(e) => handleFileList(e.target.files, 'add')}
        />
      </div>
      {preview &&
        uploadFiles.map((file, index) => (
          <PreviewImage
            key={`preview_${index}`}
            file={file}
            size={size}
            style={previewStyle}
            gap={gap}
            onRemove={handleRemoveFile} // 수정된 onRemove 핸들러 사용
          />
        ))}
    </div>
  );
};

export default UploadFile;
